import React, { useEffect, useState, useMemo } from "react";
import { IoIosAddCircleOutline } from "react-icons/io";
import { FaSearch } from "react-icons/fa";
import { LuFileEdit } from "react-icons/lu";
import { FaRegTrashCan } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import Pagination from "../../utils/Pagination";
import { RiSortAlphabetAsc, RiSortAlphabetDesc } from "react-icons/ri";
import {
  createBlockEmails,
  fetchBlockEmails,
  fetchBlockEmailsById,
  updateBlockEmails,
  deleteBlockEmails,
  fetchBlocks,
  fetchDistrictsData,
} from "../../services/api";
import AlertMsg from "../../components/AlertMsg/AlertMsg";
import Loading from "../../components/Loading/Loading";

const BlockEmails = () => {
  const [BlockEmails, setBlockEmails] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const [selectedBlocktId, setSelectedBlocktId] = useState("");
  const [blocktOptions, setBlockOptions] = useState([]);
  const [selectedDistrictstId, setSelectedDistrictstId] = useState("");
  const [districtsOptions, setDistrictsOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const blocksPerPage = 10;
  const [sortAscending, setSortAscending] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: 0,
    emailId: "",
    districtId: 0,
    blockId: 0,
    activeStatus: true,
  });

  const [formErrors, setFormErrors] = useState({
    id: 0,
    emailId: "",
    districtId: 0,
    blockId: 0,
    activeStatus: true,
  });

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.emailId.trim()) {
      newErrors.emailId = "Email is required";
      isValid = false;
    } else if (!emailPattern.test(formData.emailId)) {
      newErrors.emailId = "Invalid email format";
      isValid = false;
    }

    // Validate districtId
    if (!formData.districtId) {
      newErrors.districtId = "Please select a District.";
      isValid = false;
    }

    // Validate blockId
    if (!formData.blockId) {
      newErrors.blockId = "Please select a Block.";
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const [alert, setAlert] = useState({ visible: false, message: "", type: "" });

  const fetchBlockEmailData = async () => {
    setLoading(true);
    try {
      const response = await fetchBlockEmails();
      setBlockEmails(response.data);
    } catch (error) {
      console.error("Error fetching Block Emails data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlock = async () => {
    try {
      const response = await fetchBlocks();
      setBlockOptions(response.data);
    } catch (error) {
      console.error("Error fetching block data:", error);
    }
  };
  const fetchDistrict = async () => {
    try {
      const response = await fetchDistrictsData();
      setDistrictsOptions(response.data);
    } catch (error) {
      console.error("Error fetching district data:", error);
    }
  };

  useEffect(() => {
    fetchBlockEmailData();
    fetchBlock();
    fetchDistrict();
  }, []);

  const toggleModal = () => {
    handleCancel();
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      setFormData({
        id: 0,
        emailId: "",
        districtId: 0,
        blockId: 0,
        activeStatus: true,
      });
      setErrors({
        id: 0,
        emailId: "",
        districtId: 0,
        blockId: 0,
        activeStatus: true,
      });
    }
  };

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
    setFormData({
      id: 0,
      emailId: "",
      districtId: 0,
      blockId: 0,
      activeStatus: true,
    });
    setFormErrors({
      id: 0,
      emailId: "",
      districtId: 0,
      blockId: 0,
      activeStatus: true,
    });
    fetchBlockEmailData();
  };

  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
    setBlockToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "radio" || type === "checkbox" ? checked : value;

    console.log(`Changing ${name} to ${newValue}`); // Debug log

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleBlockChange = (e) => {
    const selectedBlockId = e.target.value;
    setSelectedBlocktId(selectedBlockId);
    setFormData((prevData) => ({
      ...prevData,
      blockId: selectedBlockId,
    }));
  };
  const handleDistrictChange = (e) => {
    const selectedDistrictId = e.target.value;
    setSelectedDistrictstId(selectedDistrictId);
    setFormData((prevData) => ({
      ...prevData,
      districtId: selectedDistrictId,
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleSortByName = () => {
    setSortAscending(!sortAscending); // Toggle sorting order
  };
  const filteredBlockEmailsStatus = useMemo(() => {
    return BlockEmails.filter(
      (block) =>
        block.emailId &&
        block.emailId.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => {
      return sortAscending
        ? a.emailId.localeCompare(b.emailId)
        : b.emailId.localeCompare(a.emailId);
    });
  }, [BlockEmails, searchTerm, sortAscending]);

  const pageCount = Math.ceil(filteredBlockEmailsStatus.length / blocksPerPage);

  const getCurrentPageBlocks = () => {
    const startIndex = currentPage * blocksPerPage;
    const endIndex = startIndex + blocksPerPage;
    return filteredBlockEmailsStatus.slice(startIndex, endIndex);
  };

  const handleCancel = () => {
    setFormData({});
    setIsEditModalOpen(false);
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setBlockToDelete(null);
    setFormErrors({});
  };
  const resetForm = () => {
    setFormData({});
    setFormErrors({});
  };
  const handleAddBlock = async () => {
    if (!validateForm()) {
      console.log("Form validation failed", formErrors);
      return;
    }
    setIsModalOpen(false);
    setLoading(true);
    try {
      const response = await createBlockEmails(formData);
      console.log(response.data);

      setAlert({
        visible: true,
        message: "Block Emails added successfully!",
        type: "success",
      });
      fetchBlockEmailData();
      resetForm();
      handleCancel();
    } catch (error) {
      console.error("Error adding BlockE mailss:", error);
      setAlert({
        visible: true,
        message: "Error adding Block Emails. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditBlock = async (id) => {
    try {
      const response = await fetchBlockEmailsById(id);
      const BlockEmailsData = response.data;

      setFormData(BlockEmailsData);
      setSelectedBlocktId(BlockEmailsData.blockId);
      setSelectedDistrictstId(BlockEmailsData.districtId);
      setIsEditModalOpen(true);
    } catch (error) {
      console.error("Error fetching Block Emails details:", error);
    }
  };

  console.log(formData);

  const handleSaveEditBlock = async () => {
    if (!validateForm()) {
      return;
    }
    setIsEditModalOpen(false);
    setLoading(true);
    try {
      const response = await updateBlockEmails(formData.id, formData);
      console.log(response.data);

      handleCancel();
      setAlert({
        visible: true,
        message: "Block Emails updated successfully!",
        type: "success",
      });
      fetchBlockEmailData();
      resetForm();
    } catch (error) {
      console.error("Error updating BlockEmails:", error);
      setAlert({
        visible: true,
        message: "Error adding BlockEmails. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlock = async () => {
    if (!blockToDelete) return;
    setIsDeleteModalOpen(false);
    setLoading(true);
    try {
      await deleteBlockEmails(blockToDelete.id);
      handleCancel();
      setAlert({
        visible: true,
        message: "Block Emails deleted successfully!",
        type: "success",
      });
      fetchBlockEmailData();
    } catch (error) {
      console.error("Error deleting BlockEmails:", error);
      setAlert({
        visible: true,
        message: "Error deleting BlockEmails. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIconClick = (block) => {
    setBlockToDelete(block);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className="houseState">
      {loading && <Loading />}
      {!loading && (
        <div className="houseState-container box-shadow">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}
          <div className="container-head p-15">
            <h2>Block Emails</h2>
            <div className="btns">
              <span className="add-btn" onClick={toggleModal}>
                <IoIosAddCircleOutline />
                <p>Add Block Emails</p>
              </span>
            </div>
          </div>
          <div className="table-filters">
            <div className="search-filter">
              <label htmlFor="searchName">
                <FaSearch />
              </label>
              <input
                type="text"
                id="searchName"
                placeholder="Search by Block Emails"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div className="houseState-content" style={{ overflowX: "auto" }}>
            <div className="table-container">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th
                      onClick={handleSortByName}
                      style={{ cursor: "pointer" }}
                    >
                      Block Emails{" "}
                      {sortAscending ? (
                        <RiSortAlphabetAsc />
                      ) : (
                        <RiSortAlphabetDesc />
                      )}
                    </th>
                    <th>Block</th>
                    <th>District</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageBlocks().length > 0 ? (
                    getCurrentPageBlocks().map((item) => (
                      <tr key={item.id}>
                        <td className="text-left">{item.emailId}</td>
                        <td className="text-left">
                          {" "}
                          {
                            blocktOptions.find(
                              (blocks) => blocks.id === item.blockId
                            )?.name
                          }
                        </td>
                        <td className="text-left">
                          {" "}
                          {
                            districtsOptions.find(
                              (dist) => dist.id === item.districtId
                            )?.name
                          }
                        </td>
                        <td className="text-center">
                          <p
                            className={
                              item.activeStatus ? "active" : "inactive"
                            }
                          >
                            {item.activeStatus ? "Active" : "Inactive"}
                          </p>
                        </td>
                        <td className="text-center">
                          <div className="action-btn">
                            <span
                              className="btn edit-btn"
                              onClick={() => handleEditBlock(item.id)}
                            >
                              <LuFileEdit />
                            </span>
                            <span
                              className="btn delete-btn"
                              onClick={() => handleDeleteIconClick(item)}
                            >
                              <FaRegTrashCan />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No Block Emails status found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination
                pageCount={pageCount}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add BlockEmails Modal */}
      {isModalOpen && (
        <div className="screen">
          <div className="screen-container">
            <div className="add-form-head">
              <h3>Add Block Emails</h3>
              <span className="close-icon" onClick={toggleModal}>
                <ImCross />
              </span>
            </div>
            <div className="add-forms">
              <div className="add-form">
                <label htmlFor="emailId"> Email Id</label>
                <input
                  type="text"
                  name="emailId"
                  placeholder="Enter Email "
                  value={formData.emailId}
                  onChange={handleInputChange}
                  className={formErrors.emailId ? "input-error" : ""}
                />
              </div>

              <div className="add-form">
                <label htmlFor="blockId">Block Name</label>
                <select
                  value={formData.blockId}
                  onChange={handleBlockChange}
                  className={formErrors.blockId ? "input-error" : ""}
                >
                  <option value="">Select Blocks</option>
                  {blocktOptions.map((blocks) => (
                    <option key={blocks.id} value={blocks.id}>
                      {blocks.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="add-form">
                <label htmlFor="districtId">District Name</label>
                <select
                  value={formData.districtId}
                  onChange={handleDistrictChange}
                  className={formErrors.districtId ? "input-error" : ""}
                >
                  <option value="">Select District</option>
                  {districtsOptions.map((dist) => (
                    <option key={dist.id} value={dist.id}>
                      {dist.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="btn-container">
                <div className="addforbtns">
                  <div className="btn-cancel" onClick={toggleModal}>
                    Cancel
                  </div>
                  <button
                    className="btn"
                    onClick={handleAddBlock}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit BlockEmails Modal */}
      {isEditModalOpen && (
        <div className="screen">
          <div className="screen-container">
            <div className="add-form-head">
              <h3>Edit Block Emails</h3>
              <span className="close-icon" onClick={toggleEditModal}>
                <ImCross />
              </span>
            </div>
            <div className="add-forms">
              <div className="add-form">
                <label htmlFor="emailid"> Email ID</label>
                <input
                  type="email"
                  name="emailId"
                  value={formData.emailId} // Ensure this is the right state
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  className={formErrors.emailId ? "input-error" : ""}
                />
              </div>

              <div className="add-form">
                <label htmlFor="blockId">Block Name</label>
                <select
                  name="blockId"
                  value={selectedBlocktId}
                  onChange={handleBlockChange}
                  className={formErrors.blockId ? "input-error" : ""}
                >
                  <option value="" disabled>
                    Select Block Name
                  </option>
                  {blocktOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="add-form">
                <label htmlFor="districtId">Districts Name</label>
                <select
                  name="districtId"
                  value={selectedDistrictstId}
                  onChange={handleDistrictChange}
                  className={formErrors.districtId ? "input-error" : ""}
                >
                  <option value="" disabled>
                    Select District Name
                  </option>
                  {districtsOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="add-form">
                <label htmlFor="activeStatus">Active Status:</label>
                <div className="custom-radio-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="activeStatus"
                      value={true}
                      checked={formData.activeStatus === true}
                      onChange={handleInputChange}
                      className="custom-radio-input"
                    />
                    Active
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="activeStatus"
                      value={false}
                      checked={formData.activeStatus === false}
                      onChange={handleInputChange}
                      className="custom-radio-input"
                    />
                    Inactive
                  </label>
                </div>
              </div>

              <div className="btn-container">
                <div className="addforbtns">
                  <div className="btn-cancel" onClick={toggleEditModal}>
                    Cancel
                  </div>
                  <button
                    className="btn"
                    onClick={handleSaveEditBlock}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && blockToDelete && (
        <div className="screen">
          <div className="screen-container delete-popup">
            <h2>Are you sure you want to delete?</h2>
            <p>This action is permanent and cannot be undone.</p>

            <button
              onClick={handleDeleteBlock}
              className="delete btn"
              disabled={isLoading}
            >
              {isLoading ? "Deleting..." : "Yes, Delete"}
            </button>
            <button className="btn-cancel" onClick={toggleDeleteModal}>
              No, Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockEmails;
